import React, {Fragment, useState} from 'react';
import CaptureViewer from '..';
import { ResourceStatus } from '../../../types/resource';
import { GlobalAction } from '../../../types/rx-actions';
import { ConfigState, ConfigType, SensorConfigActionTypes } from '../../../types/sensor-configuration';
import styles from '../styles.scss';
import {
  convertToTimelineEvent,
  PlaybackState,
  ViewerPlaybackControls,
} from '../ViewerPlaybackControls';

type SingleCaptureViewerProps = {
  configState: ConfigState,
  dispatch: (action: GlobalAction) => void,
  capturePlayerVideoRef: HTMLVideoElement,
  setCapturePlayerVideoRef: React.Dispatch<React.SetStateAction<HTMLVideoElement>>,
}

const SingleCaptureViewer: React.FunctionComponent<SingleCaptureViewerProps> = ({
  configState,
  dispatch,
  capturePlayerVideoRef,
  setCapturePlayerVideoRef,
}) => {
  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    seek: 0,
    bufferedRanges: [],
  });

  return (
    configState.config.status === ResourceStatus.COMPLETE &&
    configState.selectedCapture.status === ResourceStatus.COMPLETE
  ) ? (
    configState.captures.selectedId !== null ? (
      <Fragment>
        <div className={styles.captureViewerInner}>
          <CaptureViewer
            width={480}
            readOnly={
              configState.config.data.workingConfig.type ===
              ConfigType.RAW
            }
            capture={configState.selectedCapture.data}
            initialStartTimeSeconds={
              configState.captures.initialStartTimeSeconds
            }
            showMaxPixelIntensityOverlay
            configState={configState.config.data}
            onConfigChange={updatedConfig => {
              if (
                configState.config.status !==
                ResourceStatus.COMPLETE
              ) {
                return;
              }
              if (
                configState.config.data.workingConfig.type ===
                  ConfigType.TRIGGER_LINE_TOP ||
                configState.config.data.workingConfig.type ===
                  ConfigType.TRIGGER_LINE_BOTTOM
              ) {
                dispatch({
                  type:
                    SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE,
                  x0: Math.floor(updatedConfig.trigger_line.x0_position_override),
                  x1: Math.floor(updatedConfig.trigger_line.x1_position_override),
                  y0: Math.floor(updatedConfig.trigger_line.y0_position_override),
                  y1: Math.floor(updatedConfig.trigger_line.y1_position_override),
                });
              } else {
                dispatch({
                  type:
                    SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_SINGULARITY_REGION,
                  region: updatedConfig.singularity.region,
                  senseiExpression:
                    updatedConfig.singularity.sensei_expression,
                });
              }
            }}
            onVideoRefChanged={r => {
              setCapturePlayerVideoRef(r);

              // When changing the video ref, the seek position changes. Notify the store.
              dispatch({
                type:
                  SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK,
                seek: r.currentTime,
              });
            }}
          />
        </div>
        <ViewerPlaybackControls
          keyboardShortcutsEnabled
          playbackState={playbackState}
          onSetPlaybackState={playbackState =>
            setPlaybackState(playbackState)
          }
          tags={configState.selectedCapture.data.tags
            .filter(tag => !tag.name.includes('F8'))
            .map(t => ({
              id: t.start_frame,
              secondsOffset: t.start_frame / 30,
              tag: t,
            }))}
          tracks={[
            {
              serialNumber:
                configState.config.data.sensor.serial_number,
              captureStatus: configState.selectedCapture.data.annotation_status,
              primary: true,
              videoRef: capturePlayerVideoRef,
              offsetInSeconds: 0,

              annotationEvents: convertToTimelineEvent(
                (configState.selectedCapture.data
                  .latest_report &&
                  configState.selectedCapture.data.latest_report
                    .annotated_events) ||
                  [],
              ),

              algoEvents: convertToTimelineEvent(
                (configState.selectedCapture.data
                  .latest_report &&
                  configState.selectedCapture.data.latest_report
                    .algo_events) ||
                  [],
              ),
            },
          ]}
          // When the used has finished changing the seek position, notify the store.
          onSeekFinished={seek =>
            dispatch({
              type:
                SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK,
              seek,
            })
          }
          trackLengthInSeconds={
            capturePlayerVideoRef
              ? capturePlayerVideoRef.duration
              : 0
          }
        />
      </Fragment>
    ) : (
      <p>No capture selected</p>
    )
  ) : null;
}

export default SingleCaptureViewer;
